import React, { useMemo, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import { Conversation } from '../../hooks/useChat';
import { ChatContextHardware, ChatContextRental } from '../../hooks/chat/api';
import { riderLabel } from '../../hooks/chat/mappers';
import { useMaintenanceTasksRelayQuery } from '../../hooks/maintenance/query/V2/useMaintenanceTasksRelay';
import { createTicket } from '../../api/problems';
import { getPriorityName } from '../../utils';
import CustomToast from '../../components/Redesing/custom-toast';
import { PanelSection } from './PanelSection';
import { ActionButton } from './ActionButton';

interface Props {
  conversation: Conversation;
  rental: ChatContextRental | null;
  /** The rental's own hardware, the default target of a maintenance ticket. */
  rentalHardware: ChatContextHardware | null;
  /** The hardware the rider's messages name, when it is not the rental's. */
  messageHardware: ChatContextHardware | null;
  adminEmail: string;
  /** The rental and hardware are still being read. */
  loading?: boolean;
}

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : String(error ?? '');

const hardwareName = (hardware: ChatContextHardware | null) =>
  hardware
    ? `${hardware.hardwareProduct?.name ?? 'Hardware'} #${
        hardware.numberText ?? hardware.number ?? ''
      }`
    : '';

/**
 * What a maintenance ticket is filed against: the hardware the rider wrote
 * about, else the rental's. The rental rides along only when the ticket's
 * hardware IS the rental's; a ticket on another unit names no rental.
 */
export function ticketTarget(
  rental: ChatContextRental | null,
  rentalHardware: ChatContextHardware | null,
  messageHardware: ChatContextHardware | null
): { hardware: ChatContextHardware | null; rental: ChatContextRental | null } {
  const hardware = messageHardware ?? rentalHardware;
  const paired = Boolean(hardware && rentalHardware && hardware.id === rentalHardware.id);
  return { hardware, rental: paired ? rental : null };
}

/** Why an action cannot run, or null when it can. */
export function ticketBlocker(
  hardware: ChatContextHardware | null,
  systemOldId: string | null
) {
  if (!hardware) return 'No hardware is linked to this conversation.';
  if (!hardware.oldId) return 'This hardware has no admin id to file a ticket against.';
  if (!systemOldId) return 'The hardware has no system to file the ticket in.';
  return null;
}

/**
 * Only the actions whose REST calls the rest of the admin makes the same way
 * (maintenance ticket: pages/maintenance/addticket), behind a confirmation.
 * Ending a rental, charges, refunds and wallet credit need the rental page's
 * context, so the panel links there instead of repeating them half-wired.
 */
export function QuickActions({
  conversation,
  rental,
  rentalHardware,
  messageHardware,
  adminEmail,
  loading = false,
}: Props) {
  const ticket = ticketTarget(rental, rentalHardware, messageHardware);
  const hardware = ticket.hardware;
  const systemOldId = hardware?.system?.oldId ?? ticket.rental?.system?.oldId ?? null;
  const waiting = loading ? 'Loading the rental…' : null;
  const ticketReason = waiting ?? ticketBlocker(hardware, systemOldId);

  const [ticketOpen, setTicketOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const [taskId, setTaskId] = useState('');
  const [priority, setPriority] = useState(3);
  const [description, setDescription] = useState('');

  const taskFilter = useMemo(
    () => (systemOldId ? { systemOldId } : undefined),
    [systemOldId]
  );
  const { data: rawTasks, isLoading: tasksLoading } = useMaintenanceTasksRelayQuery(
    taskFilter,
    { enabled: ticketOpen && Boolean(systemOldId) }
  );
  // Same list as the Add ticket page: admin ids, no system-only safety checks.
  const tasks = useMemo(
    () =>
      (rawTasks ?? [])
        .filter((t) => t.taskType !== 'usage_safety_check')
        .map((t) => ({ id: t.oldId || t.id, name: t.name })),
    [rawTasks]
  );

  const hardwareLabel = hardwareName(hardware);

  const openTicket = () => {
    setTaskId('');
    setPriority(3);
    setDescription(`Reported by ${riderLabel(conversation)} in support chat.`);
    setTicketOpen(true);
  };

  const submitTicket = async () => {
    if (!hardware?.oldId || !systemOldId || !taskId || !description.trim()) return;
    setBusy(true);
    try {
      await createTicket(
        {
          due_on: new Date().toISOString(),
          description: description.trim(),
          source_type: 'user_escalated',
          priority,
          communication_log: JSON.stringify({
            admin_id: adminEmail,
            created_on: new Date().toISOString(),
            message: `Support chat session ${conversation.id}`,
          }),
          maintenance_task_id: taskId,
          hardware_id: hardware.oldId,
          module_id: null,
          assignee: null,
          error_report_id: null,
          rental_id: ticket.rental?.oldId ?? null,
          system_id: systemOldId,
        },
        systemOldId
      );
      CustomToast({
        type: 'success',
        message: `Maintenance ticket created for ${hardwareLabel}`,
      });
      setTicketOpen(false);
    } catch (error) {
      CustomToast({
        type: 'error',
        message: `Could not create the ticket. ${errorText(error)}`,
        duration: 6000,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <PanelSection title="Quick actions" testId="quick-actions">
      <Stack spacing={1}>
        <ActionButton
          label="Create maintenance ticket"
          icon={<BuildIcon />}
          blocker={ticketReason}
          color="primary"
          onClick={openTicket}
        />
        <Typography variant="caption" color="text.secondary">
          Ending the rental, charges, refunds and wallet credit are on the{' '}
          {rental ? (
            <Link
              href={`/#/rentals/${rental.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              rental page
            </Link>
          ) : (
            'rental page'
          )}
          .
        </Typography>
      </Stack>

      <Dialog
        open={ticketOpen}
        onClose={busy ? undefined : () => setTicketOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create maintenance ticket</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              For {hardwareLabel}
              {hardware?.system?.name || ticket.rental?.system?.name
                ? ` in ${hardware?.system?.name || ticket.rental?.system?.name}`
                : ''}
              {ticket.rental ? ', during the rental' : ''}
            </Typography>
            <TextField
              select
              required
              label="Task"
              value={taskId}
              onChange={(e) => setTaskId(e.target.value)}
              disabled={busy}
              helperText={
                tasksLoading
                  ? 'Loading tasks…'
                  : tasks.length === 0
                  ? 'This system has no maintenance tasks yet.'
                  : undefined
              }
            >
              {!tasksLoading && tasks.length === 0 && (
                <MenuItem value="" disabled>
                  No maintenance tasks
                </MenuItem>
              )}
              {tasks.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Priority"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              disabled={busy}
            >
              {[1, 2, 3, 4, 5].map((p) => (
                <MenuItem key={p} value={p}>
                  {`${p} - ${getPriorityName(p)}`}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              label="Description"
              multiline
              minRows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTicketOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={submitTicket}
            disabled={busy || !taskId || !description.trim()}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Create ticket
          </Button>
        </DialogActions>
      </Dialog>
    </PanelSection>
  );
}
